import React, { useState, useEffect } from 'react';
import {
  X,
  Check,
  ArrowRight,
  Utensils,
  ShoppingBag,
  Heart,
  Scissors,
  Music,
  Briefcase,
  Car,
  Home,
  Dumbbell,
  GraduationCap,
  Monitor,
  Globe,
  Palette,
  Sun,
  Users,
  Dog,
} from 'lucide-react';
import { UserInterest, InterestCategory } from '../types';

interface InterestSelectionProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (interests: UserInterest[]) => void;
  currentInterests?: UserInterest[];
  title?: string;
  isOnboarding?: boolean;
}

interface CategoryOption {
  category: InterestCategory;
  name: string;
  description: string;
  icon: React.ReactNode;
}

// The 16 canonical interest categories. Personalized matching compares a
// user's interest `category` against a business's `interestCategories`, so we
// let users choose at this level directly instead of picking dozens of
// granular interests that all collapse to the same category anyway.
const CATEGORY_OPTIONS: CategoryOption[] = [
  { category: 'food_dining', name: 'Food & Dining', description: 'Restaurants, cafés, bars, bakeries, and more', icon: <Utensils className="w-5 h-5" /> },
  { category: 'shopping_retail', name: 'Shopping & Retail', description: 'Fashion, electronics, groceries, and stores', icon: <ShoppingBag className="w-5 h-5" /> },
  { category: 'health_wellness', name: 'Health & Wellness', description: 'Fitness, medical, spas, and wellness', icon: <Heart className="w-5 h-5" /> },
  { category: 'beauty_personal_care', name: 'Beauty & Personal Care', description: 'Hair, nails, makeup, and grooming', icon: <Scissors className="w-5 h-5" /> },
  { category: 'entertainment', name: 'Entertainment', description: 'Movies, music, gaming, and events', icon: <Music className="w-5 h-5" /> },
  { category: 'services_professional', name: 'Professional Services', description: 'Legal, financial, real estate, and more', icon: <Briefcase className="w-5 h-5" /> },
  { category: 'automotive', name: 'Automotive', description: 'Repair, dealerships, gas, and car care', icon: <Car className="w-5 h-5" /> },
  { category: 'home_garden', name: 'Home & Garden', description: 'Improvement, furniture, and landscaping', icon: <Home className="w-5 h-5" /> },
  { category: 'sports_fitness', name: 'Sports & Fitness', description: 'Outdoor recreation, sports, and activities', icon: <Dumbbell className="w-5 h-5" /> },
  { category: 'education_learning', name: 'Education & Learning', description: 'Schools, tutoring, classes, and lessons', icon: <GraduationCap className="w-5 h-5" /> },
  { category: 'technology', name: 'Technology', description: 'Tech services, mobile, software, and IT', icon: <Monitor className="w-5 h-5" /> },
  { category: 'travel_tourism', name: 'Travel & Tourism', description: 'Hotels, attractions, and travel services', icon: <Globe className="w-5 h-5" /> },
  { category: 'arts_culture', name: 'Arts & Culture', description: 'Museums, galleries, and performing arts', icon: <Palette className="w-5 h-5" /> },
  { category: 'spiritual_religious', name: 'Spiritual & Religious', description: 'Churches, temples, and spiritual centers', icon: <Sun className="w-5 h-5" /> },
  { category: 'community_social', name: 'Community & Social', description: 'Community centers, nonprofits, and libraries', icon: <Users className="w-5 h-5" /> },
  { category: 'pets_animals', name: 'Pets & Animals', description: 'Pet stores, vets, grooming, and dog parks', icon: <Dog className="w-5 h-5" /> },
];

const InterestSelection: React.FC<InterestSelectionProps> = ({
  isOpen,
  onClose,
  onComplete,
  currentInterests = [],
  title = "What interests you?",
}) => {
  const [selectedInterests, setSelectedInterests] = useState<UserInterest[]>([]);

  // Collapse any existing interests (including legacy granular ones) down to a
  // single entry per category, preserving the strongest weight.
  const collapseToCategories = (interests: UserInterest[]): UserInterest[] => {
    const byCategory = new Map<InterestCategory, UserInterest>();
    interests.forEach(interest => {
      const option = CATEGORY_OPTIONS.find(o => o.category === interest.category);
      if (!option) return;
      const existing = byCategory.get(option.category);
      const weight = Math.max(existing?.weight ?? 0, interest.weight ?? 3);
      byCategory.set(option.category, {
        id: option.category,
        name: option.name,
        category: option.category,
        weight,
      });
    });
    return Array.from(byCategory.values());
  };

  useEffect(() => {
    if (!isOpen) return;
    setSelectedInterests(collapseToCategories(currentInterests));
  }, [isOpen, currentInterests]);

  if (!isOpen) return null;

  const isSelected = (category: InterestCategory) =>
    selectedInterests.some(interest => interest.category === category);

  const toggleCategory = (option: CategoryOption) => {
    if (isSelected(option.category)) {
      setSelectedInterests(prev => prev.filter(interest => interest.category !== option.category));
    } else {
      setSelectedInterests(prev => [
        ...prev,
        { id: option.category, name: option.name, category: option.category, weight: 3 },
      ]);
    }
  };

  const allSelected = selectedInterests.length === CATEGORY_OPTIONS.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedInterests([]);
    } else {
      setSelectedInterests(
        CATEGORY_OPTIONS.map(option => ({
          id: option.category,
          name: option.name,
          category: option.category,
          weight: 3,
        }))
      );
    }
  };

  const handleComplete = () => {
    onComplete(selectedInterests);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Choose your interests
            </h3>
            <p className="text-gray-600 mb-4">
              Pick the categories you care about to get personalized recommendations. You can change these anytime.
            </p>
            <button
              onClick={toggleAll}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 underline"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CATEGORY_OPTIONS.map(option => (
              <button
                type="button"
                key={option.category}
                onClick={() => toggleCategory(option)}
                aria-pressed={isSelected(option.category)}
                className={`flex items-center p-3 rounded-lg border transition-all duration-200 ${isSelected(option.category)
                    ? 'border-accent-500 bg-accent-50'
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${isSelected(option.category)
                    ? 'bg-accent-600 text-white'
                    : 'bg-gray-100 text-gray-600'
                  }`}>
                  {option.icon}
                </div>
                <div className="flex-1 text-left">
                  <h5 className="font-medium text-gray-900">{option.name}</h5>
                  <p className="text-xs text-gray-600">{option.description}</p>
                </div>
                {isSelected(option.category) && (
                  <span className="ml-3 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-accent-600 text-white">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 sticky bottom-0 bg-white z-10">
          <div className="text-sm text-gray-600">
            {selectedInterests.length} interest{selectedInterests.length !== 1 ? 's' : ''} selected
          </div>

          <button
            onClick={handleComplete}
            disabled={selectedInterests.length === 0}
            className="btn-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <span>Complete</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterestSelection;
